import { workspace } from './api';
import { CERTIFICATES, CERTIFIER_STATUS } from '../containers/certificate/certificates';
import { safeGetFromLocalStorage } from '../helpers/window';
import { Notifications } from '../components/notifications/notifications';
import { TEST_FLOW } from '../stores/prescriptionStore';
import { MedFlowIntegrationConfig } from '../interfaces/medFlowIntegration';

const generateOnlyManualCertificates = (): Array<OwnedCertificate> => {
  const result = Object.keys(CERTIFICATES).reduce<Array<OwnedCertificate>>((accumulator, key) => {
    const value = CERTIFICATES[key as keyof typeof CERTIFICATES];

    return accumulator.concat({
      status: value === CERTIFICATES.MANUAL ? 'S' : 'N',
      name: value,
      certifierStatus: CERTIFIER_STATUS.ACTIVE,
      description: null,
    });
  }, []);

  return result;
};

export const PrescriberService = {
  async getConfigurations(referenceCode: string): Promise<PrescriberConfiguration> {
    const enableMedicalCertificate = safeGetFromLocalStorage('HabilitarAtestado');
    const enableExam = safeGetFromLocalStorage('HabilitarExame');
    const version = safeGetFromLocalStorage('version');

    const params = {
      referencia: referenceCode,
    };

    const { data } = await workspace.get('/parceiro/consultar', { params });
    return {
      ...data,
      NXReceitas: version !== null ? version : data.NXReceitas,
      HabilitarAtestado:
        enableMedicalCertificate !== null ? enableMedicalCertificate === 'true' : data.HabilitarAtestado,
      HabilitarExame: enableExam !== null ? enableExam === 'true' : data.HabilitarExame,
    };
  },

  async getBenefits(): Promise<Array<Benefit>> {
    const { data } = await workspace.get('/parceiro/listarbeneficios');
    return data;
  },

  async getOwnedCertificates(document: string, prescriptionId: number): Promise<Array<OwnedCertificate>> {
    if (!document) {
      return generateOnlyManualCertificates();
    }

    const params = {
      documento: document,
      idPrescricao: prescriptionId,
    };

    const { data } = await workspace.get('/prescricao/certificado-digital/usuario', {
      params,
    });

    const certificates: OwnedCertificate[] = data.certifiers;
    // @ts-ignore
    const validCertificate = certificates.find((certificate) => certificate.name === 'VALID');

    if (validCertificate) {
      certificates.push({
        status: validCertificate.status,
        name: CERTIFICATES.VALID,
        certifierStatus: validCertificate.certifierStatus,
        description: validCertificate.description,
      });
    }

    return certificates;
  },
  async getPrescriberConfigurations(prescriberId: number): Promise<PrescriberConfigurations> {
    const { data } = await workspace.get(`/medico/${prescriberId}/configuracoes`);

    return data.data;
  },
  async readPrescriberNotification(doctorId: number, notificationId: number) {
    await workspace.post(`/notificacao/medico`, { doctorId, notificationId });
  },
  async updateDoctorPreferences(doctorId: number, updatePreferences: Record<string, any>): Promise<void> {
    const { chave, valor } = updatePreferences;
    await workspace.patch(`/medico/${doctorId}/preferencias`, { chave, valor });
  },

  async updatePrescriberDialogControlledPrescriptionSigned(
    controlledPrescriptionSigned: DialogContent,
    prescriberId: number
  ): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      dialogs: { controlledPrescriptionSigned },
    });
  },

  async updateOrderMedicinePrescription(
    updateOrderMedicinePrescription: UpdateOrderMedicine,
    idPrescription: number
  ): Promise<boolean> {
    const { data } = await workspace.put(
      `/prescricao/${idPrescription}/medicamento/ordem`,
      updateOrderMedicinePrescription
    );
    return data;
  },

  async updatePrescriber(prescriberId: number, payload: any): Promise<void> {
    const { data } = await workspace.patch(`/medico/${prescriberId}`, payload);
    return data;
  },

  async updatePrescriberDialogGameficationRelease(
    gameficationRelease: DialogContent,
    prescriberId: number
  ): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      dialogs: { gameficationRelease },
    });
  },
  async updatePrescriberOpenPatientApp(value: boolean, prescriberId: number): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      openPatientApp: value,
    });
  },
  async updateIssueMedicinesInSeparateTabs(value: boolean, prescriberId: number): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      issueMedicinesInSeparateTabs: value,
    });
  },

  async getPdfLayouts(params: { prescriberId: number; prescriptionId: number }): Promise<PDFModelLayout[]> {
    const { prescriberId, prescriptionId } = params;

    const queryParams = {
      idPrescricao: prescriptionId,
    };

    const { data } = await workspace.get(`/medico/${prescriberId}/layoutpdf`, { params: queryParams });
    return data.data;
  },
  async createPdfLayout({
    prescriberId,
    pdfLayout,
    prescriptionId,
  }: {
    prescriberId: number;
    prescriptionId: number;
    pdfLayout: Pick<PDFModelLayout, 'tipo' | 'nome' | 'selecionado' | 'configuracoes'>;
  }): Promise<PDFModelLayout[]> {
    const body = { ...pdfLayout, idPrescricao: prescriptionId };

    const { data } = await workspace.post(`/medico/${prescriberId}/layoutpdf`, body);
    return data.data;
  },
  async deletePdfLayout(prescriberId: number, pdfLayoutId: number): Promise<void> {
    await workspace.delete(`/medico/${prescriberId}/layoutpdf/${pdfLayoutId}`);
  },
  async updatePdfLayout(
    prescriberId: number,
    pdfLayout: Pick<PDFModelLayout, 'tipo' | 'nome' | 'selecionado' | 'configuracoes' | 'idLayout'>
  ): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/layoutpdf/${pdfLayout.idLayout}`, { ...pdfLayout });
  },
};

export interface DialogContent {
  value: boolean;
  lastUpdated: Date;
}

export interface Dialog {
  controlledPrescriptionSigned?: DialogContent;
  gameficationRelease?: DialogContent;
}

export enum PRESCRIBER_DIALOG_TYPES {
  controlledPrescriptionSigned = 'controlledPrescriptionSigned',
}

export interface OpenRateConfiguration {
  value: string;
  range: string;
}

export interface B2CCapture {
  hasB2CAccount: boolean;
  modalAlreadyShown: boolean;
}

export interface Configurations {
  dialogs?: Dialog;
  notifications?: Notifications;
  featureNotifications?: Notifications;
  firstAccess?: boolean;
  openRate?: OpenRateConfiguration;
  openPatientApp?: boolean;
  signatureRaffleFlow?: TEST_FLOW;
  issueMedicinesInSeparateTabs?: boolean;
  permissions?: DoctorPermissions;
  b2cCapture?: B2CCapture;
}

export interface DoctorPermissions {
  Prescription?: boolean;
  Exam?: boolean;
  Referral?: boolean;
  MedicalStatement?: boolean;
  MedicalReport?: boolean;
}

export interface PrescriberConfigurations {
  Conselho: string;
  Documento: string;
  idMedico: number;
  Hash: string;
  Nome: string;
  UF: string;
  Numero: string;
  UFConselho: string;
  Especialidades: string[];
  Configuracoes?: Configurations | null;
  ExibirDataPdf: boolean;
  UltimaAssinatura: string | null;
}

export interface EasySignupStatus {
  enabled: boolean;
  data: {
    timeout: number;
    url: string;
    shouldOpenModal: boolean;
  };
}

export interface PartnerFeatures {
  // Mantém compatibilidade com as demais flags (não tipadas) já lidas de Features.
  [key: string]: any;
  medFlowIntegration?: MedFlowIntegrationConfig;
  /**
   * Quando `true`, a posologia já usada pelo prescritor volta a ser reaproveitada do
   * histórico em vez de ser reprocessada pela LLM (`POST /prescricao/posologia`).
   * Default `false`: a LLM é a única fonte dos valores calculados.
   */
  enablePosologyHistoryReuse?: boolean;
}

export interface PrescriberConfiguration {
  idParceiro: number;
  Nome: string;
  Login: string;
  UltimoAcesso: Date;
  Bloqueado: boolean;
  NumeroTentativas: number;
  idProntuarioEletronico: number;
  ExibirObservacoes: boolean;
  HabilitarExame: boolean;
  HabilitarAtestado: boolean;
  EnviarSMSPrescricao: boolean;
  AssinarViaProntuario: boolean;
  RemoverOpcaoAssinatura: boolean;
  HabilitarSalvarSemEnviar: boolean;
  NXReceitas?: string;
  LogoUrl?: string;
  BloquearEdicaoCadastroPaciente: boolean;
  Features: PartnerFeatures;
  PrimeiroAcesso: boolean;
  PoliticasDePrivacidade: string;
  HabilitarGameficacao?: boolean;
  EasySignupStatus?: EasySignupStatus;
}

export interface Benefit {
  idBeneficioProntuario: number;
  NomeBeneficio: string;
  Ativo: boolean;
  CriadoEm: Date | string;
  AtualizadoEm: Date | string;
  IdProntuarioEletronico: number;
  TextoPrefixo: string;
  IconeCabecalho: string;
  IconeEtiquetaMedicamento: string;
  PermitirFiltroNaBusca: boolean;
  CorFundoEtiqueta: string;
  CorTextoEtiqueta: string;
}

export interface UpdateOrderMedicine {
  MedicamentosOrdenados: Array<OrderedMedicine>;
}

export interface OrderedMedicine {
  idPrescricaoMedicamento: number;
  Ordem: number;
  Manual: boolean;
}

export interface OwnedCertificate {
  status: 'S' | 'N';
  name: CERTIFICATES;
  certifierStatus: CERTIFIER_STATUS;
  description: string | null;
}

export interface PdfConfigurations {
  showHeaderDoctorName: boolean;
  showHeaderCrm: boolean;
  layoutLogo: string;
  showFooterDoctorName: boolean;
  showFooterCrm: boolean;
  showFooterEstablishmentAddress: boolean;
  showFooterEstablishmentPhone: boolean;
  showFooterEstablishmentEmail?: boolean;
  fontFamily: string;
  headerTextAlign: string;
  footerTextAlign: string;
  color: string;
  marginTop: string;
  marginBottom: string;
  marginLeft: string;
  marginRight: string;
  showLogo: boolean;
  showHeaderPatientBirthDate: boolean;
}
export interface PDFModelLayout {
  atualizadoEm: Date;
  criadoEm: Date;
  configuracoes: PdfConfigurations;
  idLayout: number;
  idMedico: number;
  nome: string;
  selecionado: boolean;
  tipo: PDF_LAYOUT_MODEL_TYPE;
}

export type PDF_LAYOUT_MODEL_TYPE = 'SIMPLES' | 'CONTROLADO';


